import React, { useEffect } from 'react';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import CloseIcon from '@mui/icons-material/Close';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { store } from '@/service/store';
import { useSignals } from '@preact/signals-react/runtime';

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  drawerWidth: number;
  collapsedWidth: number;
  variant?: 'permanent' | 'temporary';
}

interface NavigationItem {
  text: string;
  path: string;
  icon: React.ReactNode;
  translationKey: string;
}

const Sidebar = ({ open, onToggle, drawerWidth, collapsedWidth, variant = 'permanent' }: SidebarProps) => {
  useSignals();

  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = variant === 'temporary';

  const customerRepo = store.customers;
  const customers = customerRepo.getCustomerList().value;
  const loading = customerRepo.isLoading.value;

  // Load customer data when component mounts
  useEffect(() => {
    customerRepo.load();
  }, []);

  // Create customer options for the Autocomplete component
  const customerOptions = customers.map((customer) => ({
    id: customer.customerNumber,
    label: `${customer.customerNumber} - ${customer.businessName}`,
  }));

  // State to control the input value
  const [inputValue, setInputValue] = React.useState('');

  // Handle customer selection
  const handleCustomerSelect = (event: React.SyntheticEvent, value: { id: string, label: string } | null) => {
    // Only proceed if value is a valid option from the list (not free text)
    if (value && typeof value === 'object' && 'id' in value) {
      // Navigate to customer detail page
      navigate(`/customers/${value.id}`);
      toast.success(t('messages.customerLoaded', { customer: value.label }));

      // Reset the input value
      setInputValue('');

      // Close the drawer on mobile after selection
      if (isMobile) {
        onToggle();
      }
    }
  };

  // Handle key press in the Autocomplete
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      // Check if the current input value exactly matches any option
      const exactMatchOption = customerOptions.find(option =>
        option.label.toLowerCase() === inputValue.toLowerCase() ||
        option.id === inputValue
      );

      // If there's an exact match, let the default behavior handle it
      if (exactMatchOption) {
        return;
      }

      // If no exact match, check if there are any filtered suggestions
      const filteredOptions = customerOptions.filter(option => {
        // Check if label contains the input text (case insensitive)
        const labelMatch = option.label.toLowerCase().includes(inputValue.toLowerCase());

        // Check if ID contains the input text (convert both to strings)
        const idString = String(option.id);
        const idMatch = idString.includes(inputValue);

        return labelMatch || idMatch;
      });

      // If there are filtered suggestions, select the first one
      if (filteredOptions.length > 0) {
        event.preventDefault(); // Prevent default to handle it ourselves
        handleCustomerSelect(event, filteredOptions[0]);
      } else {
        // If no suggestions at all, prevent the action
        event.preventDefault();
      }
    }
  };

  // Handler for navigation item clicks
  const handleNavItemClick = () => {
    // Only close the drawer if we're on mobile
    if (isMobile) {
      onToggle();
    }
  };

  const navigationItems: NavigationItem[] = [
    { text: t('navigation.dashboard'), path: '/', icon: <DashboardIcon />, translationKey: 'navigation.dashboard' },
    // { text: t('navigation.devices'), path: '/devices', icon: <Calculator size={24} />, translationKey: 'navigation.devices' },
    { text: t('navigation.customers'), path: '/customers', icon: <PeopleIcon />, translationKey: 'navigation.customers' },
    // { text: t('navigation.history'), path: '/history', icon: <HistoryIcon />, translationKey: 'navigation.history' },
  ];

  const drawerContent = (
    <>
      {variant === 'temporary' && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pt: 2,
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          mb: 1
        }}>
          <Typography variant="subtitle1" fontWeight="medium" color="text.primary">
            {t('app.title')}
          </Typography>
          <IconButton onClick={onToggle} edge="end" size="small" color="inherit">
            <CloseIcon />
          </IconButton>
        </Box>
      )}
      <Box sx={{ px: open ? 2 : 1, pt: 2, pb: 1 }}>
        {open ? (
          <Autocomplete
            fullWidth
            freeSolo
            options={customerOptions}
            loading={loading}
            value={null}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
            }}
            onChange={handleCustomerSelect}
            onKeyDown={handleKeyDown}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder={t('navigation.loadCustomer')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        ) : (
          <Tooltip title={t('navigation.loadCustomer')} placement="right">
            <IconButton
              sx={{ width: '100%', justifyContent: 'center' }}
              onClick={() => onToggle()}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Divider />
      <List>
        {navigationItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              component={NavLink}
              to={item.path}
              onClick={handleNavItemClick}
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                '&.active': {
                  bgcolor: 'action.selected',
                  borderLeft: '3px solid',
                  borderColor: 'primary.main',
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  },
                },
              }}
              selected={location.pathname === item.path}
            >
              <Tooltip title={!open ? t(item.translationKey) : ''} placement="right">
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
              </Tooltip>
              {open && <ListItemText primary={t(item.translationKey)} />}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );

  // For temporary variant (mobile), we don't need the Box wrapper
  if (variant === 'temporary') {
    return (
      <Box sx={{
        height: '100%',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {drawerContent}
      </Box>
    );
  }

  // For permanent variant (desktop)
  return (
    <Box
      component="nav"
      sx={{
        width: { sm: open ? drawerWidth : collapsedWidth },
        flexShrink: 0,
        height: '100%',
      }}
    >
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          height: '100%',
          '& .MuiDrawer-paper': {
            position: 'static',
            whiteSpace: 'nowrap',
            width: open ? drawerWidth : collapsedWidth,
            height: '100%',
            overflowX: 'hidden',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            bgcolor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
